import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import AISettingsTab from '@/components/AISettingsTab'
import { useAuthStore } from '@/stores/authStore'
import NotificationSettings from '@/components/NotificationSettings'
import LinkedInSettings from '@/components/LinkedInSettings'
import NotificationLogs from '@/components/NotificationLogs'
import { useUIStore } from '@/stores/uiStore'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Palette, Bell, Monitor, Moon, Sun, Save, Loader2, Share2, Plus, Trash2, ExternalLink, CheckCircle, Database, Package, Tag, GitBranch, Users, Target, DollarSign, Briefcase, MapPin, BarChart3, Layers, UserCheck, Phone, ArrowRight, Linkedin, Sparkles, TrendingUp } from 'lucide-react'
import ConfigCRUD from '@/components/ConfigCRUD'
import MarketAnalysisSettingsInline from '@/components/MarketAnalysisSettingsInline'
import { useSocialAccounts, useConnectSocialAccount, useDisconnectSocialAccount, type SocialAccount } from '@/hooks/useContentHub'

type Tab = 'appearance' | 'notifications' | 'linkedin' | 'social' | 'registrations' | 'ai_settings'

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: '#E4405F', icon: '📸', desc: 'Conecte sua conta Instagram para publicacao de conteudo visual' },
  { id: 'facebook', label: 'Facebook', color: '#1877F2', icon: '📘', desc: 'Conecte sua pagina Facebook para publicacao e engajamento' },
  { id: 'twitter', label: 'X (Twitter)', color: '#000000', icon: '𝕏', desc: 'Conecte sua conta X para publicacao de conteudo e monitoramento' },
]


function NotificationTabsWrapper() {
  const [subTab, setSubTab] = useState<'config' | 'logs'>('config')
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-zinc-200 dark:border-zinc-700 pb-2">
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${subTab === 'config' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400'}`}
          onClick={() => setSubTab('config')}
        >
          Configurações
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${subTab === 'logs' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400'}`}
          onClick={() => setSubTab('logs')}
        >
          Log de Envios
        </button>
      </div>
      {subTab === 'config' && <NotificationSettings />}
      {subTab === 'logs' && <NotificationLogs />}
    </div>
  )
}

export default function SettingsPage() {
  const { theme, setTheme } = useUIStore()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as Tab) || 'appearance'
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)

  useEffect(() => {
    const tab = searchParams.get('tab') as Tab
    if (tab && ['appearance', 'notifications', 'linkedin', 'social', 'registrations', 'ai_settings'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])
  const settingsUser = useAuthStore((s) => s.user)
  const hierarchyLevel = settingsUser?.role?.hierarchy_level ?? 0
  const isBDR = settingsUser?.role?.name === 'sales_rep'
  const [message, setMessage] = useState('')

  // Social accounts
  const { data: socialData } = useSocialAccounts()
  const connectMutation = useConnectSocialAccount()
  const disconnectMutation = useDisconnectSocialAccount()
  const [connectForm, setConnectForm] = useState<{ platform: string } | null>(null)
  const [newAccount, setNewAccount] = useState({ account_name: '', account_id: '', account_url: '', access_token: '', refresh_token: '' })

  function showMsg(msg: string) {
    setMessage(msg)
    setTimeout(() => setMessage(''), 3000)
  }

  async function handleConnectAccount(platform: string) {
    if (!newAccount.account_name || !newAccount.account_id) { showMsg('Preencha nome e ID da conta'); return }
    try {
      await connectMutation.mutateAsync({
        platform,
        account_name: newAccount.account_name,
        account_id: newAccount.account_id,
        account_url: newAccount.account_url || undefined,
        access_token: newAccount.access_token || undefined,
        refresh_token: newAccount.refresh_token || undefined,
      })
      showMsg(`Conta ${platform} conectada com sucesso!`)
      setConnectForm(null)
      setNewAccount({ account_name: '', account_id: '', account_url: '', access_token: '', refresh_token: '' })
    } catch { showMsg('Erro ao conectar conta') }
  }

  async function handleDisconnect(account: SocialAccount) {
    if (!confirm(`Desconectar ${account.account_name}?`)) return
    try {
      await disconnectMutation.mutateAsync(account.id)
      showMsg('Conta desconectada')
    } catch { showMsg('Erro ao desconectar') }
  }

  const connectedAccounts = socialData?.items || []

  const tabs = [
    { id: 'appearance' as Tab, label: 'Aparencia', icon: Palette },
    { id: 'notifications' as Tab, label: 'Notificacoes', icon: Bell },
    { id: 'linkedin' as Tab, label: 'LinkedIn Outreach', icon: Linkedin },
    { id: 'registrations' as Tab, label: 'Cadastros', icon: Database },
    { id: 'social' as Tab, label: 'Redes Sociais', icon: Share2 },
    ...(hierarchyLevel >= 70 || isBDR ? [{ id: 'ai_settings' as Tab, label: 'Inteligencia IA', icon: Sparkles }] : []),
  ]

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Configuracoes</h1>
        <p className="text-muted-foreground text-sm">Gerencie suas preferencias</p>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.includes('sucesso') || message.includes('desconectada') ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'}`}>
          {message}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar tabs */}
        <div className="md:w-56 flex md:flex-col gap-1">
          {tabs.map(tab => (
            <button key={tab.id} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} onClick={() => setActiveTab(tab.id)}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'appearance' && (
            <Card className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Aparencia</h2>
              <p className="text-sm text-muted-foreground">Escolha o tema da interface</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'light' as const, label: 'Claro', icon: Sun },
                  { id: 'dark' as const, label: 'Escuro', icon: Moon },
                  { id: 'system' as const, label: 'Sistema', icon: Monitor },
                ].map(t => (
                  <button key={t.id} className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${theme === t.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} onClick={() => setTheme(t.id)}>
                    <t.icon className="h-6 w-6" />
                    <span className="text-sm font-medium">{t.label}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <NotificationTabsWrapper />
          )}

          {activeTab === 'linkedin' && (
            <LinkedInSettings />
          )}

          {activeTab === 'social' && (
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-1">Integracoes de Redes Sociais</h2>
                <p className="text-sm text-muted-foreground mb-6">Conecte suas contas para publicacao e monitoramento via Content Hub</p>

                <div className="space-y-4">
                  {PLATFORMS.map(platform => {
                    const connected = connectedAccounts.filter(a => a.platform === platform.id)
                    const isConnecting = connectForm?.platform === platform.id

                    return (
                      <div key={platform.id} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: platform.color }}>
                              {platform.icon}
                            </div>
                            <div>
                              <h3 className="font-medium">{platform.label}</h3>
                              <p className="text-xs text-muted-foreground">{platform.desc}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {connected.length > 0 && (
                              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3.5 w-3.5" /> {connected.length} conectada{connected.length > 1 ? 's' : ''}
                              </span>
                            )}
                            <Button size="sm" variant={isConnecting ? 'secondary' : 'outline'} onClick={() => {
                              setConnectForm(isConnecting ? null : { platform: platform.id })
                              setNewAccount({ account_name: '', account_id: '', account_url: '', access_token: '', refresh_token: '' })
                            }}>
                              {isConnecting ? 'Cancelar' : <><Plus className="h-3.5 w-3.5 mr-1" /> Conectar</>}
                            </Button>
                          </div>
                        </div>

                        {connected.length > 0 && (
                          <div className="border-t bg-muted/30">
                            {connected.map(account => (
                              <div key={account.id} className="flex items-center justify-between px-4 py-2.5 border-b last:border-b-0">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-green-500" />
                                  <span className="text-sm font-medium">{account.account_name}</span>{account.owner_name && <span className="text-xs text-muted-foreground ml-1">(por {account.owner_name})</span>}
                                  <span className="text-xs text-muted-foreground">@{account.account_id}</span>
                                  {account.account_url && (
                                    <a href={account.account_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    {account.last_used_at ? `Ultimo uso: ${new Date(account.last_used_at).toLocaleDateString('pt-BR')}` : 'Nunca utilizada'}
                                  </span>
                                  <button onClick={() => handleDisconnect(account)} className="text-red-500 hover:text-red-700 p-1" title="Desconectar">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {isConnecting && (
                          <div className="border-t p-4 bg-muted/20 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium">Nome da Conta *</label>
                                <Input className="mt-1" placeholder="Ex: NAI IT Oficial" value={newAccount.account_name} onChange={e => setNewAccount(a => ({ ...a, account_name: e.target.value }))} />
                              </div>
                              <div>
                                <label className="text-xs font-medium">ID / Username *</label>
                                <Input className="mt-1" placeholder="Ex: nai-it" value={newAccount.account_id} onChange={e => setNewAccount(a => ({ ...a, account_id: e.target.value }))} />
                              </div>
                              <div>
                                <label className="text-xs font-medium">URL do Perfil</label>
                                <Input className="mt-1" placeholder="https://..." value={newAccount.account_url} onChange={e => setNewAccount(a => ({ ...a, account_url: e.target.value }))} />
                              </div>
                              <div>
                                <label className="text-xs font-medium">Access Token</label>
                                <Input className="mt-1" type="password" placeholder="Token de acesso da API" value={newAccount.access_token} onChange={e => setNewAccount(a => ({ ...a, access_token: e.target.value }))} />
                              </div>
                              <div>
                                <label className="text-xs font-medium">Refresh Token</label>
                                <Input className="mt-1" type="password" placeholder="Token de renovacao" value={newAccount.refresh_token} onChange={e => setNewAccount(a => ({ ...a, refresh_token: e.target.value }))} />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleConnectAccount(platform.id)} disabled={connectMutation.isPending}>
                                {connectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                                Salvar Conexao
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setConnectForm(null)}>Cancelar</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Card>

              <Card className="p-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
                <div className="flex gap-3">
                  <Share2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-200">Como funciona a integracao?</p>
                    <ul className="mt-1 text-blue-700 dark:text-blue-300 space-y-0.5 text-xs">
                      <li>1. Conecte suas contas de redes sociais acima</li>
                      <li>2. No Content Hub, crie e agende publicacoes</li>
                      <li>3. As publicacoes serao enviadas automaticamente para as plataformas conectadas</li>
                      <li>4. Acompanhe metricas de engajamento e alcance no dashboard</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'ai_settings' && (hierarchyLevel >= 70 || isBDR) && (
            <AISettingsTab />
          )}

          {activeTab === 'registrations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Cadastros do Sistema</h2>
                  <p className="text-sm text-muted-foreground">Gerencie origens, status, estagios e demais dados mestres</p>
                </div>
                <div className="flex gap-2">
                  <a href="/products" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border hover:bg-muted transition-colors">
                    Produtos & Servicos
                  </a>
                  <a href="/price-tables" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border hover:bg-muted transition-colors">
                    Tabelas de Preco
                  </a>
                  <a href="/users" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border hover:bg-muted transition-colors">
                    Usuarios
                  </a>
                </div>
              </div>
              <ConfigCRUD />
            </div>
          )}


          {activeTab === 'registrations' && (
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-1">Cadastros Basicos</h2>
                <p className="text-sm text-muted-foreground mb-6">Gerencie os dados mestres do sistema</p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                  {/* Produtos & Servicos */}
                  <a href="/products" className="group border rounded-lg p-5 hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer block">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Produtos & Servicos</h3>
                    <p className="text-xs text-muted-foreground">Catalogo de produtos, servicos, licencas e tipos de cobranca</p>
                  </a>

                  {/* Tabelas de Preco */}
                  <a href="/price-tables" className="group border rounded-lg p-5 hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer block">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Tabelas de Preco</h3>
                    <p className="text-xs text-muted-foreground">Tabelas de precos vigentes, historico e regras de precificacao</p>
                  </a>

                  {/* Origens de Lead */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Origens de Lead</h3>
                    <p className="text-xs text-muted-foreground mb-3">Canais de captacao de leads</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'LinkedIn Automation', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
                        { label: 'BDR/Parceiros', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
                        { label: 'Consultor Interno', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
                        { label: 'Website', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
                        { label: 'Evento', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300' },
                        { label: 'Indicacao', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' },
                      ].map(o => (
                        <span key={o.label} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${o.color}`}>{o.label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Status de Lead */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Status de Lead</h3>
                    <p className="text-xs text-muted-foreground mb-3">Funil de qualificacao</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'Suspect', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
                        { label: 'Prospect', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
                        { label: 'MQL', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
                        { label: 'SQL', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
                        { label: 'Oportunidade', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
                        { label: 'Cliente', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
                        { label: 'Perdido', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
                      ].map(s => (
                        <span key={s.label} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${s.color}`}>{s.label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Estagios de Oportunidade */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                        <GitBranch className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Estagios de Oportunidade</h3>
                    <p className="text-xs text-muted-foreground mb-3">Pipeline Kanban</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'Qualificado', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
                        { label: 'Proposta Enviada', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
                        { label: 'Negociacao', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
                        { label: 'Fechamento', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' },
                        { label: 'Ganho', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
                        { label: 'Perdido', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
                      ].map(s => (
                        <span key={s.label} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${s.color}`}>{s.label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Status de Proposta */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Status de Proposta</h3>
                    <p className="text-xs text-muted-foreground mb-3">Workflow de aprovacao</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'Rascunho', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
                        { label: 'Enviada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
                        { label: 'Em Analise', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
                        { label: 'Aprovada', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
                        { label: 'Rejeitada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
                        { label: 'Cancelada', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
                      ].map(s => (
                        <span key={s.label} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${s.color}`}>{s.label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Papeis de Contato */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Papeis de Contato</h3>
                    <p className="text-xs text-muted-foreground mb-3">Funcoes no processo de compra</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'Decisor' },
                        { label: 'Influenciador' },
                        { label: 'Champion' },
                        { label: 'Usuario' },
                        { label: 'Gatekeeper' },
                        { label: 'Comprador Econ.' },
                        { label: 'Comprador Tec.' },
                        { label: 'Parceiro' },
                        { label: 'Diretor' },
                      ].map(r => (
                        <span key={r.label} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300">{r.label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Jornadas */}
                  <div className="border rounded-lg p-5 bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
                        <Layers className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Tipos de Jornada</h3>
                    <p className="text-xs text-muted-foreground mb-3">Canais e tipos de jornada de vendas</p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Canais</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {['Inbound', 'Outbound', 'Parceiros'].map(c => (
                            <span key={c} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">{c}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Tipos</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {['MQL', 'SQL', 'Oportunidade', 'Onboarding'].map(t => (
                            <span key={t} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Perfis de Usuario */}
                  <a href="/users" className="group border rounded-lg p-5 hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer block">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                        <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Usuarios & Perfis</h3>
                    <p className="text-xs text-muted-foreground">Gestao de usuarios, perfis de acesso e permissoes (RBAC)</p>
                  </a>

                </div>
              </Card>

              <Card className="p-4 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                <div className="flex gap-3">
                  <Database className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-200">Sobre os cadastros</p>
                    <p className="mt-1 text-amber-700 dark:text-amber-300 text-xs">
                      Os valores de Origens, Status, Estagios, Papeis e Jornadas sao gerenciados pelo sistema. Cards com seta permitem navegacao para a tela de cadastro completa.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Analise de Mercado */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-1">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  <h2 className="text-lg font-semibold">Analise de Mercado</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-6">Configure fontes de informacao, agendamento de varreduras e visualize logs de execucao</p>
                <MarketAnalysisSettingsInline />
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
